import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { AdminPurchasesSection } from "@/components/admin/purchases/AdminPurchasesSection";
import { ADMIN_ROUTES, USER_ROUTES } from "@/routes";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getUserProfile,
  updateEventPlan,
  updateUserStatus,
  type UserProfileResponse,
  type UserProfileEvent,
  type EventPlanKey,
} from "@/services/api/management";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  User,
  Calendar,
  CreditCard,
  Save,
  Ban,
  CheckCircle2,
} from "lucide-react";
import { format } from "date-fns";
import { ro } from "date-fns/locale";

/**
 * Plan keys PlanManagementController@updateEventPlan validates against
 * (JUB-138/JUB-142). JUB-157: plans exist only per event; the account-plan
 * editor was removed from this page.
 */
const EVENT_PLAN_KEYS: EventPlanKey[] = ["free", "standard", "premium"];

function normalizeEventPlan(plan: string): EventPlanKey {
  return (EVENT_PLAN_KEYS as string[]).includes(plan) ? (plan as EventPlanKey) : "free";
}

/** Extracts the backend's error envelope message, not axios's raw "Request failed..." text. */
function extractApiErrorMessage(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
}

export default function SuperadminUserProfile() {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const currentLocale = useRouteLocale();
  const dateLocale = currentLocale === "ro" ? ro : undefined;
  usePageMeta({
    title: t("management.userProfileMetaTitle"),
    description: t("management.userProfileMetaTitle"),
    locale: currentLocale,
    path: ADMIN_ROUTES.userProfile(currentLocale, userId ?? ""),
    noindex: true,
  });
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  const [data, setData] = useState<UserProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileReloadKey, setProfileReloadKey] = useState(0);
  const [statusUpdating, setStatusUpdating] = useState(false);
  // Draft plan selection per event (event_uuid -> chosen plan), before Apply is pressed.
  const [eventPlanDrafts, setEventPlanDrafts] = useState<Record<string, EventPlanKey>>({});
  // Which event rows currently have a save in flight (event_uuid -> boolean).
  const [savingEventPlanUuids, setSavingEventPlanUuids] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/${currentLocale}/management/login`, { replace: true });
      return;
    }
    if (!user.user_admin) {
      navigate(USER_ROUTES.events(currentLocale), { replace: true });
      return;
    }
    setCheckingPermissions(false);
  }, [user, authLoading, navigate, currentLocale]);

  useEffect(() => {
    if (!userId || checkingPermissions) return;
    const id = parseInt(userId, 10);
    if (Number.isNaN(id)) {
      toast.error("Invalid user ID");
      return;
    }
    let cancelled = false;
    setLoading(true);
    getUserProfile(id)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        toast.error(msg || t("management.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, checkingPermissions, t, profileReloadKey]);

  /**
   * Grants or revokes a plan on one event (JUB-142). This is the real
   * gating mechanism — see PlanManagementController's class docblock. The
   * plan flips optimistically so the row updates immediately; on failure
   * it rolls back to the previous value.
   */
  const handleSaveEventPlan = async (ev: UserProfileEvent) => {
    const previousPlan = normalizeEventPlan(ev.plan);
    const targetPlan = eventPlanDrafts[ev.event_uuid] ?? previousPlan;
    if (targetPlan === previousPlan) return;

    setSavingEventPlanUuids((prev) => ({ ...prev, [ev.event_uuid]: true }));
    setData((prev) =>
      prev
        ? {
            ...prev,
            events: prev.events.map((e) => (e.event_uuid === ev.event_uuid ? { ...e, plan: targetPlan } : e)),
          }
        : prev
    );

    try {
      const res = await updateEventPlan(ev.event_uuid, targetPlan);
      setData((prev) =>
        prev
          ? {
              ...prev,
              events: prev.events.map((e) => (e.event_uuid === ev.event_uuid ? { ...e, plan: res.plan } : e)),
            }
          : prev
      );
      setEventPlanDrafts((prev) => {
        const next = { ...prev };
        delete next[ev.event_uuid];
        return next;
      });
      toast.success(
        t("management.eventPlanOverride.saveSuccess", { plan: t(`management.planOverride.plans.${res.plan}`) })
      );
    } catch (err) {
      // Roll back the optimistic flip.
      setData((prev) =>
        prev
          ? {
              ...prev,
              events: prev.events.map((e) => (e.event_uuid === ev.event_uuid ? { ...e, plan: previousPlan } : e)),
            }
          : prev
      );
      toast.error(extractApiErrorMessage(err) || t("management.eventPlanOverride.saveError"));
    } finally {
      setSavingEventPlanUuids((prev) => ({ ...prev, [ev.event_uuid]: false }));
    }
  };

  const handleToggleStatus = async () => {
    if (!data || !userId) return;
    const id = parseInt(userId, 10);
    if (Number.isNaN(id)) return;
    const nextActive = !data.user.user_active;
    setStatusUpdating(true);
    try {
      const res = await updateUserStatus(id, nextActive);
      setData((prev) => (prev ? { ...prev, user: { ...prev.user, user_active: res.data.active } } : prev));
      toast.success(res.data.active ? t("management.userActivated") : t("management.userDeactivated"));
    } catch (err) {
      const errorCode = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      const msg =
        errorCode === "cannot_deactivate_self"
          ? t("management.cannotDeactivateSelf")
          : (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast.error(msg || t("management.userStatusError"));
    } finally {
      setStatusUpdating(false);
    }
  };

  if (checkingPermissions || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
        <AdminHeader currentLocale={currentLocale} active="management" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </main>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
        <AdminHeader currentLocale={currentLocale} active="management" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[60vh]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
        <AdminHeader currentLocale={currentLocale} active="management" />
        <main className="container mx-auto px-4 py-8">
          <p className="text-muted-foreground">{t("management.userNotFound") ?? "User not found."}</p>
          <Link to={ADMIN_ROUTES.management(currentLocale)}>
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("management.backToManagement") ?? "Back to management"}
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  const { user: profileUser, events } = data;
  // An admin cannot deactivate their own account (enforced server-side too); hide
  // the toggle entirely on your own profile rather than show a button that always errors.
  const isSelf =
    (!!user?.uuid && user.uuid === profileUser.uuid) ||
    (!!user?.user_id && user.user_id === profileUser.user_id) ||
    (!!user?.userId && user.userId === profileUser.user_id);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
      <AdminHeader currentLocale={currentLocale} active="management" />
      <main className="container mx-auto px-4 py-8 min-h-[calc(100vh-4rem)]">
        <Link to={ADMIN_ROUTES.management(currentLocale)}>
          <Button variant="ghost" size="sm" className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("management.backToManagement") ?? "Back to management"}
          </Button>
        </Link>

        <Card className="mb-8">
          <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {profileUser.user_name || profileUser.user_email}
              </CardTitle>
              {/* a div, not CardDescription: badges are block elements and may not sit inside a <p> (JUB-204) */}
              <div className="text-sm text-muted-foreground">
                {profileUser.user_email}
                {profileUser.user_active ? (
                  <Badge className="ml-2 bg-green-600 hover:bg-green-600/90 text-white border-transparent">
                    {t("management.active")}
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="ml-2">
                    {t("management.inactive")}
                  </Badge>
                )}
                {profileUser.stripe_connected ? (
                  <Badge variant="default" className="ml-2">
                    <CreditCard className="h-3 w-3 mr-1" />
                    {t("management.stripeConnected")}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="ml-2">{t("management.stripeNotConnected")}</Badge>
                )}
              </div>
            </div>
            {isSelf ? null : (
              <Button
                variant={profileUser.user_active ? "destructive" : "default"}
                size="sm"
                onClick={handleToggleStatus}
                disabled={statusUpdating}
                data-testid="superadmin-user-status-toggle"
              >
                {statusUpdating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : profileUser.user_active ? (
                  <Ban className="h-4 w-4 mr-2" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                {profileUser.user_active ? t("management.deactivateUser") : t("management.activateUser")}
              </Button>
            )}
          </CardHeader>
        </Card>

        {/*
          JUB-157: plans belong to events. The legacy account-plan override
          (PATCH /management/accounts/{id}/plan) is no longer offered here: it
          granted nothing to any event. Per-event grants live in the events
          table below; purchases and transfers in this section.
        */}
        <AdminPurchasesSection
          userId={profileUser.user_id}
          events={events}
          onChanged={() => setProfileReloadKey((k) => k + 1)}
        />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {t("management.userEvents") ?? "Events"} ({events.length})
            </CardTitle>
            <CardDescription>
              {t("management.userEventsDescription") ?? "All events for this user with stats."}{" "}
              {t("management.eventPlanOverride.governsNote")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t("management.noEvents") ?? "No events."}</p>
              </div>
            ) : (
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>{t("management.userEventsColumns.event")}</TableHead>
                      <TableHead>{t("management.userEventsColumns.url")}</TableHead>
                      <TableHead>{t("management.userEventsColumns.date")}</TableHead>
                      <TableHead>{t("management.userEventsColumns.plan")}</TableHead>
                      <TableHead>{t("management.eventPlanOverride.columnTitle")}</TableHead>
                      <TableHead>{t("management.userEventsColumns.guestsRsvps")}</TableHead>
                      <TableHead>{t("management.userEventsColumns.gifts")}</TableHead>
                      <TableHead>{t("management.userEventsColumns.status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((ev) => {
                      const effectivePlan = normalizeEventPlan(ev.plan);
                      const draftPlan = eventPlanDrafts[ev.event_uuid] ?? effectivePlan;
                      const rowSaving = !!savingEventPlanUuids[ev.event_uuid];
                      return (
                        <TableRow key={ev.event_id}>
                          <TableCell className="font-medium">
                            {ev.event_name}
                            {ev.trashed && (
                              <Badge variant="destructive" className="ml-2" data-testid="admin-event-trashed">
                                {t("management.purchases.trashed")}
                              </Badge>
                            )}
                            {ev.archived && (
                              <Badge variant="secondary" className="ml-2">
                                {t("management.purchases.archived")}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <code className="text-xs">{ev.event_url || ev.event_uuid}</code>
                          </TableCell>
                          <TableCell>{ev.event_date ? format(new Date(ev.event_date), "PP", { locale: dateLocale }) : "—"}</TableCell>
                          <TableCell>
                            <Badge variant={effectivePlan === "premium" ? "default" : effectivePlan === "standard" ? "secondary" : "outline"}>
                              {t(`management.planOverride.plans.${effectivePlan}`)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {/*
                                Native <select>, not the shared <Select> used above: this
                                cell lives inside the events table's overflow-x-auto
                                wrapper, and CSS forces overflow-y to auto whenever
                                overflow-x isn't visible — the shared component's
                                absolutely-positioned dropdown panel gets clipped by that
                                wrapper instead of floating over the page. A native select
                                renders as an OS-level popup that ignores ancestor overflow.
                              */}
                              <select
                                value={draftPlan}
                                onChange={(e) =>
                                  setEventPlanDrafts((prev) => ({
                                    ...prev,
                                    [ev.event_uuid]: e.target.value as EventPlanKey,
                                  }))
                                }
                                disabled={rowSaving}
                                aria-label={t("management.eventPlanOverride.selectLabel")}
                                data-testid={`event-plan-select-${ev.event_uuid}`}
                                className="flex h-10 w-[130px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {EVENT_PLAN_KEYS.map((key) => (
                                  <option key={key} value={key}>
                                    {t(`management.planOverride.plans.${key}`)}
                                  </option>
                                ))}
                              </select>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSaveEventPlan(ev)}
                                disabled={rowSaving || draftPlan === effectivePlan}
                                data-testid={`event-plan-save-${ev.event_uuid}`}
                                aria-label={t("management.eventPlanOverride.apply")}
                              >
                                {rowSaving ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            {ev.guests_count} / {ev.confirmed_guests_count}
                          </TableCell>
                          <TableCell>
                            {ev.gifts_total} / {ev.gifts_active} / {ev.gifts_inactive}
                          </TableCell>
                          <TableCell>
                            {ev.event_active ? (
                              <Badge variant="default">{t("management.active")}</Badge>
                            ) : (
                              <Badge variant="secondary">{t("management.inactive")}</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
